import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import SandboxBanner from './SandboxBanner';
import { sandboxApi, getSandboxToken } from './sandboxApi';

const initialForm = {
  titulo: '',
  descripcion: '',
  tipo_postulante: 'ambos',
  estado: 'abierta',
  cupos: 20,
  fecha_inicio: '',
  fecha_cierre: '',
};

export default function SandboxFuncionarioDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [convocatorias, setConvocatorias] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const [filterConv, setFilterConv] = useState('all');

  const loadAll = useCallback(async () => {
    try {
      const [s, c, p] = await Promise.all([
        sandboxApi.stats(),
        sandboxApi.listConvocatorias(),
        sandboxApi.listPostulaciones(),
      ]);
      setStats(s.data);
      setConvocatorias(c.data);
      setPostulaciones(p.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        navigate('/sandbox/funcionario');
      } else {
        toast.error('Error cargando datos del sandbox');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getSandboxToken()) {
      navigate('/sandbox/funcionario');
      return;
    }
    loadAll();
  }, [loadAll, navigate]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      titulo: c.titulo || '',
      descripcion: c.descripcion || '',
      tipo_postulante: c.tipo_postulante || 'ambos',
      estado: c.estado || 'abierta',
      cupos: c.cupos || 0,
      fecha_inicio: c.fecha_inicio || '',
      fecha_cierre: c.fecha_cierre || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await sandboxApi.updateConvocatoria(editing.id, form);
        toast.success('Convocatoria actualizada');
      } else {
        await sandboxApi.createConvocatoria(form);
        toast.success('Convocatoria creada');
      }
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta convocatoria sandbox?')) return;
    try {
      await sandboxApi.deleteConvocatoria(id);
      toast.success('Eliminada');
      loadAll();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restablecer el sandbox a datos demo? Se borrarán convocatorias y postulaciones de prueba.'))
      return;
    try {
      await sandboxApi.reset();
      toast.success('Sandbox restablecido');
      loadAll();
    } catch {
      toast.error('Error al restablecer');
    }
  };

  const filteredPostulaciones =
    filterConv === 'all' ? postulaciones : postulaciones.filter((p) => p.convocatoria_id === filterConv);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SandboxBanner rolePath="funcionario" />
        <div className="flex items-center justify-center h-64">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SandboxBanner rolePath="funcionario" />
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Panel Funcionario · Sandbox
            </h1>
            <p className="text-sm text-slate-600">
              Crea convocatorias, revisa postulaciones y practica el flujo completo.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="sandbox-reset-btn"
              variant="outline"
              onClick={handleReset}
            >
              Restablecer datos demo
            </Button>
            <Button
              data-testid="sandbox-create-convocatoria-btn"
              className="bg-sky-600 hover:bg-sky-700"
              onClick={openCreate}
            >
              + Nueva convocatoria
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div
          data-testid="sandbox-stats"
          className="grid grid-cols-2 md:grid-cols-6 gap-3"
        >
          <StatCard label="Convocatorias" value={stats?.total_convocatorias} />
          <StatCard label="Postulaciones" value={stats?.total_postulaciones} />
          <StatCard label="Postuladas" value={stats?.postuladas} accent="text-sky-600" />
          <StatCard label="Revisadas" value={stats?.revisadas} accent="text-amber-600" />
          <StatCard label="Aprobadas" value={stats?.aprobadas} accent="text-emerald-600" />
          <StatCard label="Rechazadas" value={stats?.rechazadas} accent="text-rose-600" />
        </div>

        <Tabs defaultValue="convs">
          <TabsList>
            <TabsTrigger data-testid="tab-convs" value="convs">
              Convocatorias ({convocatorias.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-posts" value="posts">
              Postulaciones ({postulaciones.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="convs" className="mt-4">
            <div className="space-y-3">
              {convocatorias.length === 0 && (
                <p className="text-sm text-slate-500">Aún no hay convocatorias.</p>
              )}
              {convocatorias.map((c) => (
                <Card
                  key={c.id}
                  data-testid={`conv-card-${c.id}`}
                  className="hover:shadow-md transition"
                >
                  <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{c.titulo}</h3>
                        <Badge variant="secondary">{c.tipo_postulante}</Badge>
                        <Badge
                          className={
                            c.estado === 'abierta'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-slate-200 text-slate-700'
                          }
                        >
                          {c.estado}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                        {c.descripcion}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Cupos: {c.cupos ?? '—'} · {c.fecha_inicio || '?'} →{' '}
                        {c.fecha_cierre || '?'}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        data-testid={`conv-edit-${c.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(c)}
                      >
                        Editar
                      </Button>
                      <Button
                        data-testid={`conv-delete-${c.id}`}
                        size="sm"
                        variant="outline"
                        className="text-rose-600 hover:bg-rose-50 border-rose-300"
                        onClick={() => handleDelete(c.id)}
                      >
                        Eliminar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="posts" className="mt-4">
            <div className="mb-3 flex items-center gap-3">
              <Label className="text-sm">Filtrar por convocatoria:</Label>
              <Select value={filterConv} onValueChange={setFilterConv}>
                <SelectTrigger
                  data-testid="filter-conv-select"
                  className="w-72"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {convocatorias.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.titulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Postulante</th>
                    <th className="px-3 py-2 text-left">Documento</th>
                    <th className="px-3 py-2 text-left">Convocatoria</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left">Puntaje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPostulaciones.length === 0 && (
                    <tr>
                      <td className="px-3 py-6 text-center text-slate-500" colSpan={5}>
                        No hay postulaciones.
                      </td>
                    </tr>
                  )}
                  {filteredPostulaciones.map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`post-row-${p.id}`}
                      className="border-t hover:bg-slate-50"
                    >
                      <td className="px-3 py-2">{p.user_nombre}</td>
                      <td className="px-3 py-2 text-slate-600">{p.user_documento}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {p.convocatoria_titulo}
                      </td>
                      <td className="px-3 py-2">
                        <Badge
                          className={
                            p.estado === 'aprobada'
                              ? 'bg-emerald-100 text-emerald-700'
                              : p.estado === 'rechazada'
                              ? 'bg-rose-100 text-rose-700'
                              : p.estado === 'revisada'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-sky-100 text-sky-700'
                          }
                        >
                          {p.estado}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">{p.puntaje ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Editar convocatoria sandbox' : 'Nueva convocatoria sandbox'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="conv-titulo">Título *</Label>
              <Input
                id="conv-titulo"
                data-testid="conv-titulo-input"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="conv-desc">Descripción</Label>
              <Textarea
                id="conv-desc"
                data-testid="conv-desc-input"
                rows={3}
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo de postulante</Label>
                <Select
                  value={form.tipo_postulante}
                  onValueChange={(v) => setForm({ ...form, tipo_postulante: v })}
                >
                  <SelectTrigger data-testid="conv-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="natural">Persona Natural</SelectItem>
                    <SelectItem value="juridica">Persona Jurídica</SelectItem>
                    <SelectItem value="ambos">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  value={form.estado}
                  onValueChange={(v) => setForm({ ...form, estado: v })}
                >
                  <SelectTrigger data-testid="conv-estado-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="abierta">Abierta</SelectItem>
                    <SelectItem value="cerrada">Cerrada</SelectItem>
                    <SelectItem value="borrador">Borrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="conv-cupos">Cupos</Label>
                <Input
                  id="conv-cupos"
                  type="number"
                  min={0}
                  value={form.cupos}
                  onChange={(e) =>
                    setForm({ ...form, cupos: parseInt(e.target.value || '0', 10) })
                  }
                />
              </div>
              <div>
                <Label>Inicio</Label>
                <Input
                  type="date"
                  value={form.fecha_inicio}
                  onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Cierre</Label>
                <Input
                  type="date"
                  value={form.fecha_cierre}
                  onChange={(e) => setForm({ ...form, fecha_cierre: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </Button>
              <Button
                data-testid="conv-submit-btn"
                type="submit"
                className="bg-sky-600 hover:bg-sky-700"
              >
                {editing ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent = 'text-slate-900' }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-bold ${accent}`}>{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}
